import {
    ContentMeasuredStyles,
    DropdownContentWidth,
    PreferredPosition,
} from '@/features/ui/components/Dropdown/types'
import { measureContentDimensions } from '@/features/ui/components/Dropdown/utils/measureContentDimensions'
import { RefObject, useState } from 'react'
import { View, useWindowDimensions } from 'react-native'

const anchorAndContentGap = 8

export const useContentMeasuredStyles = (
    anchorRef: RefObject<View | null>,
    contentRef: RefObject<View | null>,
    preferredPosition: PreferredPosition,
    contentWidth?: DropdownContentWidth
): [ContentMeasuredStyles | undefined, () => Promise<void>] => {
    const [styles, setStyles] = useState<ContentMeasuredStyles>()

    const { height: windowHeight, width: windowWidth } = useWindowDimensions()

    const measure = async () => {
        const contentDimensions = await measureContentDimensions(contentRef)

        anchorRef.current?.measureInWindow((anchorX, anchorY, anchorWidth, anchorHeight) => {
            const measuredContentWidth = contentDimensions.width
            const resolvedContentWidth =
                contentWidth === 'anchor'
                    ? anchorWidth
                    : typeof contentWidth === 'number'
                      ? contentWidth
                      : measuredContentWidth

            const getTop = () => {
                const topPositionAbove = -contentDimensions.height - anchorAndContentGap
                const topPositionAboveRelativeToWindow = anchorY + topPositionAbove
                const topPositionBelow = anchorHeight + anchorAndContentGap
                const topPositionBelowRelativeToWindow = anchorY + topPositionBelow

                const fitsAbove = topPositionAboveRelativeToWindow > 0
                const fitsBelow =
                    topPositionBelowRelativeToWindow + contentDimensions.height < windowHeight

                if (preferredPosition === 'above') {
                    return fitsAbove ? topPositionAbove : topPositionBelow
                }

                return fitsBelow ? topPositionBelow : topPositionAbove
            }

            const getLeft = () => {
                const leftPositionStart = 0
                const leftPositionStartRelativeToWindow = anchorX
                const leftPositionEnd = anchorWidth - resolvedContentWidth
                const leftPositionEndRelativeToWindow = anchorX + leftPositionEnd

                const fitsStart =
                    leftPositionStartRelativeToWindow + resolvedContentWidth < windowWidth
                const fitsEnd = leftPositionEndRelativeToWindow + resolvedContentWidth < windowWidth

                if (!fitsStart && !fitsEnd) return anchorWidth / 2 - resolvedContentWidth / 2

                return fitsStart ? leftPositionStart : leftPositionEnd
            }

            setStyles({
                top: getTop(),
                left: getLeft(),
                ...((contentWidth === 'anchor' || typeof contentWidth === 'number') && {
                    width: resolvedContentWidth,
                }),
            })
        })
    }

    return [styles, measure]
}
